import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  findings as findingsApi,
  notifications as notifsApi,
} from "../lib/api";
import { useApp } from "../context/AppContext";
import {
  ListTodo, Clock, Check, X as XIcon, Bell, ArrowRight, Inbox,
} from "lucide-react";
import { localizedCategory } from "../lib/i18nContent";

const STATUS_VISUAL = {
  to_do: { icon: ListTodo, classes: "bg-stone-100 text-stone-700" },
  in_progress: { icon: Clock, classes: "bg-amber-50 text-amber-700" },
  done: { icon: Check, classes: "bg-emerald-50 text-emerald-700" },
  cannot_do: { icon: XIcon, classes: "bg-red-50 text-red-700" },
};

export default function MyFindingsWidget() {
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const navigate = useNavigate();
  const [findings, setFindings] = useState([]);
  const [notifs, setNotifs] = useState([]);

  const load = async () => {
    if (!user?.id) return;
    try {
      const [fs, ns] = await Promise.all([
        findingsApi.list({ view_all: true }),
        notifsApi.list({ unread_only: true, limit: 100 }),
      ]);
      setFindings(
        (fs || []).filter(
          (f) => f.responsible_id === user.id && f.status !== "done",
        ),
      );
      // /api/notifications returns a paginated envelope { items, total, unread, ... }
      // — unwrap defensively so a shape change on the server never crashes the app.
      const notifItems = Array.isArray(ns) ? ns : (ns?.items || []);
      setNotifs(notifItems.filter((n) => !n.read));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const unreadCount = useMemo(() => notifs.length, [notifs]);

  const markAllRead = async () => {
    try {
      await notifsApi.markAllRead();
      setNotifs([]);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Card className="canales-card p-5" data-testid="my-findings-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="canales-overline flex items-center gap-2">
            <Inbox size={12} /> {t("my_findings.title")}
          </div>
          <h3 className="font-display text-xl mt-1">{t("my_findings.subtitle")}</h3>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold ring-1 ring-amber-600/20 hover:bg-amber-100 transition-colors"
            data-testid="my-findings-mark-read"
          >
            <Bell size={12} />
            {t("my_findings.unread_notifications", { n: unreadCount })}
          </button>
        )}
      </div>

      {findings.length === 0 ? (
        <p className="text-sm text-stone-500 mt-4 py-6 text-center">
          {t("my_findings.empty")}
        </p>
      ) : (
        <ul className="mt-4 space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="my-findings-list">
          {findings.slice(0, 6).map((f) => {
            const v = STATUS_VISUAL[f.status] || STATUS_VISUAL.to_do;
            const Icon = v.icon;
            const url = f.share_token ? `/finding/${f.share_token}` : `/findings`;
            return (
              <li
                key={f.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-stone-200 hover:border-stone-300 hover:bg-stone-50 transition-colors"
                data-testid={`my-finding-${f.id}`}
              >
                <span className={`shrink-0 w-7 h-7 rounded-md grid place-items-center ${v.classes}`}>
                  <Icon size={13} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.description}</div>
                  <div className="text-[11px] text-stone-500 truncate">
                    {f.store_name} · {localizedCategory(f.category, i18n.language)}
                    {f.commitment_date ? ` · ${f.commitment_date}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => navigate(url)}
                  className="text-xs font-semibold text-[#1E3A8A] hover:underline shrink-0 inline-flex items-center gap-1"
                >
                  {t("my_findings.open")} <ArrowRight size={11} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {findings.length > 6 && (
        <div className="mt-3 text-right">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/findings")}
            className="gap-1.5"
          >
            {t("my_findings.view_all")} ({findings.length}) <ArrowRight size={12} />
          </Button>
        </div>
      )}
    </Card>
  );
}
